import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";

/** Convert a custom username into the synthetic email format required by Supabase Auth */
function usernameToEmail(username: string): string {
  const sanitized = username.trim().toLowerCase().replace(/\s+/g, "");
  if (sanitized.includes("@")) return sanitized;
  return `${sanitized}@users.local`;
}

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultMode?: "login" | "register";
}

type ModalMode = "login" | "register";

type SuccessState = "idle" | "registered-auto" | "registered-verify";

export default function AuthModal({
  isOpen,
  onClose,
  onSuccess,
  defaultMode = "login",
}: AuthModalProps) {
  const [mode, setMode] = useState<ModalMode>(defaultMode);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [withdrawPassword, setWithdrawPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [successState, setSuccessState] = useState<SuccessState>("idle");
  // Track whether user just submitted the form so we can auto-redirect
  // even if a timeout error was thrown before onAuthStateChange fired.
  const [justSubmitted, setJustSubmitted] = useState(false);

  const { signIn, signUp, authActionLoading, isLoggedIn } = useAuth();
  const navigate = useNavigate();

  // Auto-close & redirect whenever auth state becomes logged-in
  // while the modal is still open after a submission attempt.
  useEffect(() => {
    if (isLoggedIn && isOpen && justSubmitted && successState === "registered-auto") {
      const timer = setTimeout(() => {
        resetFields();
        onSuccess?.();
        onClose();
        navigate("/");
        setJustSubmitted(false);
        setSuccessState("idle");
      }, 1800);
      return () => clearTimeout(timer);
    }
  }, [isLoggedIn, isOpen, justSubmitted, successState]);

  // Reset flags whenever modal closes.
  useEffect(() => {
    if (!isOpen) {
      setJustSubmitted(false);
      setSuccessState("idle");
      setError("");
    }
  }, [isOpen]);

  const resetFields = useCallback(() => {
    setUsername("");
    setPassword("");
    setWithdrawPassword("");
    setShowPassword(false);
    setPhone("");
    setName("");
    setAgree(false);
    setError("");
  }, []);

  const switchMode = useCallback((newMode: ModalMode) => {
    setMode(newMode);
    setError("");
    setSuccessState("idle");
  }, []);

  if (!isOpen) return null;

  const isLogin = mode === "login";
  const isRegister = mode === "register";

  // Validation: passwords must be different in register mode
  const passwordsMatchError =
    isRegister &&
    password &&
    withdrawPassword &&
    password === withdrawPassword;

  // Check if form is valid for submit
  const isFormValid = isLogin
    ? username.trim().length >= 4 && password.length >= 4 && password.length <= 12
    : name.trim() &&
      username.trim().length >= 4 &&
      username.trim().length <= 12 &&
      phone.trim().length >= 9 &&
      password.length >= 4 &&
      password.length <= 12 &&
      withdrawPassword.length >= 4 &&
      withdrawPassword.length <= 6 &&
      password !== withdrawPassword &&
      agree;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessState("idle");

    const trimmedUsername = username.trim();
    const trimmedPassword = password.trim();

    if (isRegister && trimmedPassword === withdrawPassword) {
      setError("登入密碼與提領密碼不能相同");
      return;
    }

    setJustSubmitted(true);

    try {
      if (isLogin) {
        const emailForLogin = usernameToEmail(trimmedUsername);
        console.log("[AuthModal] 登入帳號:", emailForLogin);
        await signIn(trimmedUsername, trimmedPassword);
        resetFields();
        onSuccess?.();
        onClose();
        navigate("/");
      } else if (isRegister) {
        const emailForRegister = usernameToEmail(trimmedUsername);
        console.log("[AuthModal] 註冊帳號:", emailForRegister);
        const result = await signUp(trimmedUsername, trimmedPassword, name.trim(), withdrawPassword, phone.trim());
        if (result.session) {
          // Auto-login in progress — wait for isLoggedIn useEffect to redirect
          setSuccessState("registered-auto");
        } else {
          // Email confirmation may be required
          setSuccessState("registered-verify");
          setJustSubmitted(false);
        }
      }
    } catch (err: unknown) {
      setJustSubmitted(false);
      setSuccessState("idle");
      const msg = err instanceof Error ? err.message : "操作失敗，請稍後再試";
      setError(msg);
      // useAuth 內部已經彈出 alert，此處同步顯示於表單中以確保用戶可見
    }
  };

  // ── Success View ────────────────────────────────
  if (successState !== "idle") {
    const isAutoLogin = successState === "registered-auto";
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-2xl w-full max-w-[420px] mx-4 overflow-hidden animate-fadeIn">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors z-10"
          >
            <i className="ri-close-line text-stone-400" />
          </button>

          <div className="px-6 pt-12 pb-10 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-5">
              <i className="ri-checkbox-circle-line text-emerald-600 text-3xl" />
            </div>
            <h2 className="font-display text-xl font-bold text-stone-900 mb-2">
              {isAutoLogin ? "註冊成功！" : "註冊完成"}
            </h2>
            <p className="font-body text-sm text-stone-500 max-w-xs leading-relaxed">
              {isAutoLogin
                ? "帳號已建立，已自動登入，即將為你跳轉到首頁..."
                : "帳號已建立成功！請使用你的帳號與密碼登入。"}
            </p>
            {isAutoLogin && (
              <div className="mt-6">
                <i className="ri-loader-4-line text-stone-400 animate-spin text-xl" />
              </div>
            )}
            {!isAutoLogin && (
              <button
                onClick={() => {
                  setSuccessState("idle");
                  switchMode("login");
                }}
                className="mt-6 font-body text-sm font-semibold px-8 py-2.5 rounded-full bg-rose-800 text-white hover:bg-rose-900 transition-colors whitespace-nowrap"
              >
                前往登入
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl w-full max-w-[420px] mx-4 max-h-[90vh] overflow-y-auto animate-fadeIn">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors"
        >
          <i className="ri-close-line text-stone-400" />
        </button>

        {/* Header */}
        <div className="text-center px-6 pt-7 pb-4">
          <div className="w-12 h-12 rounded-xl bg-rose-800 flex items-center justify-center mx-auto mb-3">
            <i className="ri-skull-line text-white text-xl" />
          </div>
          <h2 className="font-display text-xl font-bold text-stone-900">
            {isLogin ? "歡迎回來" : "加入 Star Skull"}
          </h2>
          <p className="font-body text-xs text-stone-500 mt-1">
            {isLogin
              ? "輸入帳號與密碼登入"
              : "註冊成為會員，解鎖專屬體驗"}
          </p>
        </div>

        {/* Mode tabs */}
        <div className="flex p-1 rounded-full bg-stone-100 mx-6 mb-4">
          <button
            onClick={() => switchMode("login")}
            className={`flex-1 font-body text-xs font-semibold py-2 rounded-full transition-all whitespace-nowrap ${
              isLogin
                ? "bg-white text-stone-900 shadow-sm"
                : "text-stone-500 hover:text-stone-700"
            }`}
          >
            登入
          </button>
          <button
            onClick={() => switchMode("register")}
            className={`flex-1 font-body text-xs font-semibold py-2 rounded-full transition-all whitespace-nowrap ${
              isRegister
                ? "bg-white text-stone-900 shadow-sm"
                : "text-stone-500 hover:text-stone-700"
            }`}
          >
            註冊
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 pb-7 flex flex-col gap-3">
          {/* Name - register only */}
          {isRegister && (
            <div>
              <label className="font-body text-xs font-medium text-stone-600 mb-1 block">
                姓名 <span className="text-rose-600">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-stone-400">
                  <i className="ri-user-line text-sm" />
                </div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="請輸入您的真實姓名"
                  className="w-full font-body text-sm pl-9 pr-4 py-2.5 rounded-xl border border-stone-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all placeholder:text-stone-300"
                  required={isRegister}
                />
              </div>
            </div>
          )}

          {/* Username / Account */}
          <div>
            <label className="font-body text-xs font-medium text-stone-600 mb-1 block">
              帳號 <span className="text-rose-600">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-stone-400">
                <i className="ri-account-circle-line text-sm" />
              </div>
              <input
                type="text"
                value={username}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 12);
                  setUsername(val);
                }}
                placeholder="請輸入 4-12 位帳號（英文+數字）"
                className="w-full font-body text-sm pl-9 pr-4 py-2.5 rounded-xl border border-stone-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all placeholder:text-stone-300"
                required
                minLength={4}
                maxLength={12}
              />
              {username.trim() && (
                <p className="font-body text-[11px] text-stone-400 mt-1 ml-1">
                  此帳號將用於登入與識別你的會員身份
                </p>
              )}
            </div>
          </div>

          {/* Phone Number - register only */}
          {isRegister && (
            <div>
              <label className="font-body text-xs font-medium text-stone-600 mb-1 block">
                手機號碼 <span className="text-rose-600">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-stone-400">
                  <i className="ri-smartphone-line text-sm" />
                </div>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^\d]/g, "").slice(0, 10);
                    setPhone(val);
                  }}
                  placeholder="請輸入您的手機號碼"
                  className="w-full font-body text-sm pl-9 pr-4 py-2.5 rounded-xl border border-stone-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all placeholder:text-stone-300"
                  required={isRegister}
                  minLength={9}
                  maxLength={10}
                  inputMode="numeric"
                />
              </div>
            </div>
          )}

          {/* Password */}
          <div>
            <label className="font-body text-xs font-medium text-stone-600 mb-1 block">
              密碼 <span className="text-rose-600">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-stone-400">
                <i className="ri-lock-line text-sm" />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 12);
                  setPassword(val);
                }}
                placeholder="請設置 4-12 位登入密碼（英文+數字）"
                className="w-full font-body text-sm pl-9 pr-9 py-2.5 rounded-xl border border-stone-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all placeholder:text-stone-300"
                required
                minLength={4}
                maxLength={12}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-stone-400 hover:text-stone-600"
              >
                <i className={showPassword ? "ri-eye-line" : "ri-eye-off-line"} />
              </button>
            </div>
          </div>

          {/* Withdraw Password - register only */}
          {isRegister && (
            <div>
              <label className="font-body text-xs font-medium text-stone-600 mb-1 block">
                提領密碼 <span className="text-rose-600">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-stone-400">
                  <i className="ri-shield-keyhole-line text-sm" />
                </div>
                <input
                  type="password"
                  value={withdrawPassword}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 6);
                    setWithdrawPassword(val);
                  }}
                  placeholder="請設置 4-6 位提領密碼（英文+數字）"
                  className="w-full font-body text-sm pl-9 pr-4 py-2.5 rounded-xl border border-stone-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all placeholder:text-stone-300"
                  required={isRegister}
                  minLength={4}
                  maxLength={6}
                />
              </div>
            </div>
          )}

          {/* Agreement - register only */}
          {isRegister && (
            <label className="flex items-start gap-2 cursor-pointer mt-0.5">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-stone-300 text-rose-800 focus:ring-rose-200 accent-rose-800"
              />
              <span className="font-body text-[11px] text-stone-500 leading-relaxed">
                我已閱讀並同意
                <a href="/" className="text-rose-700 hover:underline" onClick={(e) => e.preventDefault()}>
                  認購協議
                </a>
                與
                <a href="/" className="text-rose-700 hover:underline" onClick={(e) => e.preventDefault()}>
                  隱私政策
                </a>
              </span>
            </label>
          )}

          {/* Password match error */}
          {passwordsMatchError && (
            <p className="font-body text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg flex items-center gap-1.5">
              <i className="ri-error-warning-line" />
              登入密碼與提領密碼不能相同
            </p>
          )}

          {/* General error message */}
          {error && !passwordsMatchError && (
            <p className="font-body text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg flex items-center gap-1.5">
              <i className="ri-error-warning-line" />
              {error}
            </p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={authActionLoading || !isFormValid}
            className="w-full font-body text-sm font-semibold py-3 rounded-full bg-rose-800 text-white hover:bg-rose-900 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed mt-1"
          >
            {authActionLoading
              ? "處理中..."
              : isLogin
                ? "登入"
                : "建立帳戶"}
          </button>

          {/* Bottom links */}
          <div className="text-center mt-0.5">
            <p className="font-body text-xs text-stone-400">
              {isLogin ? "還沒有帳號？" : "已經有帳號？"}
              <button
                type="button"
                onClick={() => switchMode(isLogin ? "register" : "login")}
                className="text-rose-700 font-semibold ml-1 hover:underline"
              >
                {isLogin ? "立即註冊" : "立即登入"}
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}